import { Subscription } from '@bpm-web-app/api-client';
import { PackageIdPreviewBody, SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { useRef } from 'react';
import useSWR from 'swr';

export function useGetSubscriptionPackages(platform: SubscriptionPackage.PlatformEnum | 'stream' | 'supreme' | 'create', disabled?: boolean) {
    const { data, error } = useSWR(
        [
            `supreme-get-subscription-packages-${platform}`,
            {
                persist: false,
                platform,
            },
        ],
        () => Subscription.getSubscriptionPackages(platform as any),
        {
            isPaused: () => !!disabled,
        }
    );

    return {
        data,
        isLoading: !error && !data,
        error,
    };
}

export function useGetMeSubscription(platform?: Parameters<typeof Subscription.getMeSubscription>['0'], disabled?: boolean) {
    const { data, error, mutate } = useSWR(
        [
            `${platform}-get-user-subscriptions`,
            {
                persist: false,
                platform,
            },
        ],
        () => (platform ? Subscription.getMeSubscription(platform) : null),
        {
            isPaused: () => !!disabled || !platform,
        }
    );

    return {
        data,
        isLoading: !error && !data,
        error,
        mutate,
    };
}

export function useGetMeSubscriptionUnCached(platform: Parameters<typeof Subscription.getMeSubscription>['0'], disabled?: boolean) {
    const random = useRef(Date.now());
    const { data, error, mutate } = useSWR(
        [
            `${platform}-get-user-subscriptions-no-cache-${random}`,
            { persist: false },
        ],
        () => (platform ? Subscription.getMeSubscription(platform) : null),
        {
            isPaused: () => !!disabled,
        }
    );

    return {
        data,
        isLoading: !error && !data,
        error,
        mutate,
    };
}

export function useGetSubscriptionPackage(packageId?: SubscriptionPackage['id'], enabled = true) {
    const { data, error } = useSWR(
        [
            `get-subscription-${packageId}`,
            {
                persist: false,
                packageId,
            },
        ],
        () => packageId ? Subscription.getSubscriptionPackage(packageId) : null,
        { isPaused: () => !enabled || !packageId }
    );
    return {
        data,
        isLoading: !error && !data && Boolean(packageId),
        error,
    };
}

export function useGetSubscribePreview(packageId: SubscriptionPackage['id'], body?: PackageIdPreviewBody) {
    const { data, error } = useSWR(
        [
            `supreme-get-subscribe-preview-${packageId}`,
            {
                persist: false,
                packageId,
                coupon: body?.coupon
            },
        ],
        () => Subscription.subscribePreview(packageId, body),
        { shouldRetryOnError: false, onError: () => { } }
    );
    return {
        data,
        isLoading: !error && !data,
        error,
    };
}

export function useCancelSubscription() {
    return (platform: SubscriptionPackage.PlatformEnum) => Subscription.cancelSubscription(platform);
}

export function useStartSubscription() {
    return (platform: SubscriptionPackage.PlatformEnum) => Subscription.startSubscriptionProcess(platform);
}
